// helpers/tracking/destinations.ts

import {GtagFn, TtqInstance} from '@/types';
import {toGa4EventName, toGa4Params, toTikTokEventName, toTikTokParams} from '@/helpers/tracking/eventMap';
import {getTrackingPath} from '@/helpers/tracking/getTrackingPath';
import {ConsentChoices, getConsentState, removeCookies} from '@/helpers/tracking/consent';

/**
 * Multi-Destination-Tracking (GA4 & TikTok) – analog zum Meta-Pixel:
 * - consent-gated laden (siehe bindDestinationsConsent)
 *   GA4 → Kategorie "Statistik", TikTok → Kategorie "Marketing"
 * - jede Destination wird pro ID nur einmal initialisiert
 * - Widerruf: Destination wird deaktiviert und ihre First-Party-Cookies gelöscht
 * - Pfad-Gating: aktuell nur /bewerben* (Recruiting-Journey)
 */

const ga4LoadedMap: Record<string, boolean> = {};
const ttqLoadedMap: Record<string, boolean> = {};
/** GA4 per Widerruf deaktiviert (ga-disable-<ID>), bis erneut eingewilligt wird */
const ga4DisabledMap: Record<string, boolean> = {};
/** TikTok per Widerruf deaktiviert (ttq.revokeConsent), bis erneut eingewilligt wird */
const ttqRevokedMap: Record<string, boolean> = {};

/** Von GA4 (gtag.js) gesetzte First-Party-Cookies */
export const GA4_COOKIE_NAMES = ['_ga'];
export const GA4_COOKIE_PREFIXES = ['_ga_'];
/** Von TikTok Pixel bzw. captureTtclid gesetzte First-Party-Cookies */
export const TIKTOK_COOKIE_NAMES = ['_ttp', 'ttclid', '_tt_enable_cookie'];
export const TIKTOK_COOKIE_PREFIXES = ['ttcsid'];

/* -------------------- Pfad-Gating -------------------- */

export function getGa4IdForPath(pathname: string): string | null {
    if (pathname === '/bewerben' || pathname.startsWith('/bewerben/')) {
        return process.env.VUE_APP_GA4_MEASUREMENT_ID || null;
    }
    return null;
}

export function getTikTokPixelCodeForPath(pathname: string): string | null {
    if (pathname === '/bewerben' || pathname.startsWith('/bewerben/')) {
        return process.env.VUE_APP_TIKTOK_RECRUITING_PIXEL_CODE || null;
    }
    return null;
}

/* -------------------- Loader: GA4 (gtag.js) -------------------- */

export function loadGa4(measurementId: string): void {
    const w = window as Window & {
        dataLayer?: unknown[];
        gtag?: GtagFn;
        _ga4ScriptAppended?: boolean;
    };

    if (ga4LoadedMap[measurementId]) return;
    // Harte Sperre: ohne Statistik-Einwilligung nichts laden (§ 25 TDDDG)
    if (!getConsentState().statistics) return;

    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
        // WICHTIG: gtag muss das arguments-Objekt pushen, kein Array!
        w.gtag = function gtag() {
            // eslint-disable-next-line prefer-rest-params
            (w.dataLayer as unknown[]).push(arguments);
        } as unknown as GtagFn;
    }

    if (!w._ga4ScriptAppended) {
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        const first = document.getElementsByTagName('script')[0];
        first?.parentNode?.insertBefore(s, first);
        w._ga4ScriptAppended = true;
    }

    try {
        // Consent Mode: Wir fragen nur Statistik ab – Google-Werbefunktionen
        // (Ads-Cookies, Personalisierung, Signals) bleiben deaktiviert.
        w.gtag?.('consent', 'default', {
            analytics_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
        });
        w.gtag?.('js', new Date());
        // send_page_view: false → PageView wird manuell über das bestehende
        // PAGE_VIEW-Event geschickt (SPA-Kontrolle, Dedup mit Meta-Flow)
        w.gtag?.('config', measurementId, {
            send_page_view: false,
            anonymize_ip: true,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
        });
        ga4LoadedMap[measurementId] = true;
    } catch {
        // do nothing
    }
}

/* -------------------- Loader: TikTok Pixel (ttq) -------------------- */

export function loadTikTokPixel(pixelCode: string): void {
    const w = window as Window & {
        TiktokAnalyticsObject?: string;
        ttq?: TtqInstance;
    };

    if (ttqLoadedMap[pixelCode]) return;
    // Harte Sperre: ohne Marketing-Einwilligung nichts laden (§ 25 TDDDG)
    if (!getConsentState().marketing) return;

    // Offizielles TikTok-Snippet, portiert auf TS
    const t = 'ttq';
    w.TiktokAnalyticsObject = t;
    // ttq startet als Queue-Array (TikTok-Snippet), Methoden werden unten dran gehängt
    const ttq: any = (w.ttq = w.ttq || ([] as unknown as TtqInstance));
    ttq.methods = [
        'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once',
        'ready', 'alias', 'group', 'enableCookie', 'disableCookie',
        'holdConsent', 'revokeConsent', 'grantConsent',
    ];
    ttq.setAndDefer = function (target: any, method: string) {
        target[method] = function (...args: unknown[]) {
            target.push([method].concat(args as never[]));
        };
    };
    for (let i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function (pixel: string) {
        const e = ttq._i?.[pixel] || [];
        for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
        return e;
    };
    ttq.load = function (pixel: string, options?: Record<string, unknown>) {
        const src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        ttq._i = ttq._i || {};
        ttq._i[pixel] = [];
        ttq._i[pixel]._u = src;
        ttq._t = ttq._t || {};
        ttq._t[pixel] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[pixel] = options || {};
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = `${src}?sdkid=${pixel}&lib=${t}`;
        const first = document.getElementsByTagName('script')[0];
        first?.parentNode?.insertBefore(s, first);
    };

    try {
        ttq.load(pixelCode);
        ttq.page();
        ttqLoadedMap[pixelCode] = true;
    } catch {
        // do nothing
    }
}

/* -------------------- ttclid-Capture (für S2S-Attribution) -------------------- */

/**
 * TikTok-Click-ID aus der URL in ein First-Party-Cookie persistieren,
 * damit der Tracker (CAPI-Proxy) sie serverseitig an die
 * TikTok Events API weiterreichen kann. Nur nach Consent aufrufen!
 */
export function captureTtclid(): void {
    if (!getConsentState().marketing) return;
    try {
        // ttclid kann vor dem Hash (?ttclid=…#/bewerben) ODER im Hash-Query
        // (#/bewerben?ttclid=…) stehen – beide Varianten prüfen
        let ttclid = new URLSearchParams(window.location.search).get('ttclid');
        if (!ttclid) {
            const hash = window.location.hash;
            const qIdx = hash.indexOf('?');
            if (qIdx >= 0) {
                ttclid = new URLSearchParams(hash.slice(qIdx)).get('ttclid');
            }
        }
        if (!ttclid) return;
        const maxAge = 60 * 60 * 24 * 28; // 28 Tage (TikTok-Attributionsfenster)
        document.cookie = `ttclid=${encodeURIComponent(ttclid)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    } catch {
        // do nothing
    }
}

/* -------------------- Consent-Binding -------------------- */

/** GA4 nach Widerruf stoppen (offizielles Opt-out-Flag) und Cookies entfernen. */
export function disableGa4(measurementId: string): void {
    const w = window as unknown as Record<string, unknown> & { gtag?: GtagFn };
    try {
        w[`ga-disable-${measurementId}`] = true;
        if (ga4LoadedMap[measurementId]) w.gtag?.('consent', 'update', {analytics_storage: 'denied'});
    } catch {
        // do nothing
    }
    ga4DisabledMap[measurementId] = true;
    removeCookies(GA4_COOKIE_NAMES, GA4_COOKIE_PREFIXES);
}

function enableGa4(measurementId: string): void {
    const w = window as unknown as Record<string, unknown> & { gtag?: GtagFn };
    try {
        w[`ga-disable-${measurementId}`] = false;
        if (ga4LoadedMap[measurementId]) w.gtag?.('consent', 'update', {analytics_storage: 'granted'});
    } catch {
        // do nothing
    }
    ga4DisabledMap[measurementId] = false;
}

type TtqConsentApi = { revokeConsent?: () => void; grantConsent?: () => void };

/** TikTok nach Widerruf stoppen (Consent-API des Pixels) und Cookies entfernen. */
export function revokeTikTok(pixelCode: string | null): void {
    const w = window as Window & { ttq?: TtqInstance };
    try {
        if (w.ttq) (w.ttq as unknown as TtqConsentApi).revokeConsent?.();
    } catch {
        // do nothing
    }
    if (pixelCode) ttqRevokedMap[pixelCode] = true;
    removeCookies(TIKTOK_COOKIE_NAMES, TIKTOK_COOKIE_PREFIXES);
}

function grantTikTok(pixelCode: string): void {
    if (!ttqRevokedMap[pixelCode]) return;
    const w = window as Window & { ttq?: TtqInstance };
    try {
        if (w.ttq) (w.ttq as unknown as TtqConsentApi).grantConsent?.();
    } catch {
        // do nothing
    }
    ttqRevokedMap[pixelCode] = false;
}

/**
 * GA4 & TikTok analog zu bindConsent (Meta) an den Cookie-Consent koppeln.
 * GA4 folgt der Kategorie "Statistik", TikTok der Kategorie "Marketing".
 * Geladen wird nur, wenn der Pfad gematcht wird und die Kategorie erlaubt
 * ist – sofort und bei jeder Consent-Änderung. Bei Widerruf wird die
 * Destination gestoppt und ihre Cookies werden gelöscht.
 */
export function bindDestinationsConsent(
    getConsent: () => ConsentChoices,
    onChange: (cb: (choices: ConsentChoices) => void) => void,
): void {
    // Hash-Routing: Pfad aus dem Hash lesen, nicht aus pathname!
    const path = getTrackingPath();
    const ga4Id = getGa4IdForPath(path);
    const ttPixelCode = getTikTokPixelCodeForPath(path);
    if (!ga4Id && !ttPixelCode) return; // keine Destination für diese Seite

    const activate = (choices: ConsentChoices) => {
        try {
            if (ga4Id) {
                if (choices.statistics) {
                    enableGa4(ga4Id);
                    loadGa4(ga4Id);
                } else if (ga4LoadedMap[ga4Id]) {
                    disableGa4(ga4Id);
                }
            }
            if (ttPixelCode) {
                if (choices.marketing) {
                    grantTikTok(ttPixelCode);
                    loadTikTokPixel(ttPixelCode);
                    captureTtclid();
                } else if (ttqLoadedMap[ttPixelCode]) {
                    revokeTikTok(ttPixelCode);
                }
            }
        } catch {
            // no crash
        }
    };

    try {
        activate(getConsent());
    } catch {
        // no crash
    }
    onChange((choices) => activate(choices));
}

/* -------------------- Track-APIs (werden vom CookieManager gerufen) -------------------- */

/** GA4-Journey-Event senden (No-op, wenn Destination inaktiv/nicht geladen). */
export function ga4Track(
    pathname: string,
    metaEventName: string,
    params?: Record<string, unknown>,
): void {
    const ga4Id = getGa4IdForPath(pathname);
    if (!ga4Id || !ga4LoadedMap[ga4Id] || ga4DisabledMap[ga4Id]) return;
    const w = window as Window & { gtag?: GtagFn };
    if (!w.gtag) return;
    try {
        w.gtag('event', toGa4EventName(metaEventName), toGa4Params(metaEventName, params));
    } catch {
        // do nothing
    }
}

/**
 * TikTok-Browser-Event senden (nur gemappte Journey-Events, siehe eventMap.ts).
 * event_id wird mitgegeben → Dedup mit dem serverseitigen Events-API-Event.
 */
export function tiktokTrack(
    pathname: string,
    metaEventName: string,
    params?: Record<string, unknown>,
    eventId?: string,
): void {
    const pixelCode = getTikTokPixelCodeForPath(pathname);
    if (!pixelCode || !ttqLoadedMap[pixelCode] || ttqRevokedMap[pixelCode]) return;
    const ttEvent = toTikTokEventName(metaEventName);
    if (!ttEvent) return;
    const w = window as Window & { ttq?: TtqInstance };
    if (!w.ttq) return;
    try {
        w.ttq.track(
            ttEvent,
            toTikTokParams(metaEventName, params),
            eventId ? {event_id: eventId} : undefined,
        );
    } catch {
        // do nothing
    }
}

/* -------------------- TikTok Advanced Matching (ttq.identify) -------------------- */

/** SHA-256 als Hex-String (SubtleCrypto, nur im Secure Context verfügbar) */
async function sha256Hex(input: string): Promise<string | undefined> {
    try {
        if (typeof crypto === 'undefined' || !crypto.subtle) return undefined;
        const data = new TextEncoder().encode(input);
        const digest = await crypto.subtle.digest('SHA-256', data);
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    } catch {
        return undefined;
    }
}

export type TikTokIdentifyPii = {
    email?: string;
    phone?: string;
    externalId?: string;
};

/**
 * Advanced Matching: PII client-seitig SHA-256-hashen und an ttq.identify
 * übergeben (TikTok-Vorgabe: Hashing MUSS client-seitig passieren).
 * Normalisierung wie im Tracker (meta-hash.helper): E-Mail trim+lowercase,
 * Telefon nur Ziffern und '+'.
 *
 * Vor dem zugehörigen Event aufrufen – der Aufrufer (CookieManager) prüft Consent.
 */
export async function tiktokIdentify(pathname: string, pii: TikTokIdentifyPii): Promise<void> {
    const pixelCode = getTikTokPixelCodeForPath(pathname);
    if (!pixelCode || !ttqLoadedMap[pixelCode] || ttqRevokedMap[pixelCode]) return;
    const w = window as Window & { ttq?: TtqInstance };
    if (!w.ttq) return;

    const payload: Record<string, string> = {};

    if (pii.email) {
        const em = await sha256Hex(pii.email.trim().toLowerCase());
        if (em) payload.email = em;
    }
    if (pii.phone) {
        const digits = pii.phone.replace(/[^\d+]/g, '');
        const ph = digits ? await sha256Hex(digits) : undefined;
        if (ph) payload.phone_number = ph;
    }
    if (pii.externalId) {
        const eid = await sha256Hex(pii.externalId);
        if (eid) payload.external_id = eid;
    }

    if (!Object.keys(payload).length) return;
    try {
        w.ttq.identify(payload);
    } catch {
        // do nothing
    }
}
